// src/utils/cattleService.js
import {
  collection, addDoc, getDocs, updateDoc, deleteDoc,
  doc, query, where, orderBy, writeBatch, limit, startAfter,
  getCountFromServer
} from 'firebase/firestore';
import { db } from '../firebase';

const cattleCol = (uid) => collection(db, 'users', uid, 'cattle');

export async function getCattleCount(uid) {
  const snap = await getCountFromServer(cattleCol(uid));
  return snap.data().count;
}

export async function importCattleRecords(uid, records) {
  // 1. Get all existing tag numbers from the database
  const existingTagsSnap = await getDocs(cattleCol(uid));
  const existingTags = new Set(existingTagsSnap.docs.map(d => String(d.data().tagNumber).trim()));

  // 2. Filter records to prevent duplicates (from DB or within the file)
  const toImport = [];
  const seenInImport = new Set();
  const skipped = { db: 0, file: 0 };

  for (const record of records) {
    const tag = String(record.tagNumber).trim();
    if (!tag) continue; // Skip empty tags

    if (existingTags.has(tag)) {
      skipped.db++;
      continue;
    }
    if (seenInImport.has(tag)) {
      skipped.file++;
      continue;
    }

    toImport.push({ ...record, tagNumber: tag });
    seenInImport.add(tag);
  }

  if (toImport.length === 0) {
    return { success: 0, skipped };
  }

  // 3. Batch writes (max 500 per batch)
  const BATCH_SIZE = 400;
  for (let i = 0; i < toImport.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    const chunk = toImport.slice(i, i + BATCH_SIZE);
    chunk.forEach(record => {
      const ref = doc(cattleCol(uid));
      batch.set(ref, { ...record, createdAt: new Date().toISOString() });
    });
    await batch.commit();
  }
  return { success: toImport.length, skipped };
}

export async function getAllCattle(uid) {
  const snap = await getDocs(cattleCol(uid));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function checkTagExists(uid, tagNumber, excludeId = null) {
  const q = query(cattleCol(uid), where('tagNumber', '==', String(tagNumber)));
  const snap = await getDocs(q);
  if (excludeId) return snap.docs.some(d => d.id !== excludeId);
  return !snap.empty;
}

export async function addCattle(uid, record) {
  const duplicate = await checkTagExists(uid, record.tagNumber);
  if (duplicate) throw new Error(`Tag number ${record.tagNumber} already exists in the database.`);
  const ref = await addDoc(cattleCol(uid), { ...record, createdAt: new Date().toISOString() });
  return ref.id;
}

export async function updateCattle(uid, id, data) {
  if (data.tagNumber) {
    const duplicate = await checkTagExists(uid, data.tagNumber, id);
    if (duplicate) throw new Error(`Tag number ${data.tagNumber} already exists in the database.`);
  }
  await updateDoc(doc(db, 'users', uid, 'cattle', id), { ...data, updatedAt: new Date().toISOString() });
}

export async function deleteCattle(uid, id) {
  await deleteDoc(doc(db, 'users', uid, 'cattle', id));
}

export async function searchByTag(uid, tagNumber) {
  const q = query(cattleCol(uid), where('tagNumber', '==', String(tagNumber)));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}
